/**
 * NotificationService - Active drill reminder and notification system
 * Handles browser notifications, periodic checks, and drill due alerts
 */

import logger from '../utils/logger';
import { legacyGet } from '../storage/legacyUtils';

class NotificationService {
  constructor() {
    this.checkInterval = null;
    this.notificationPermission = null;
    this.lastCheckTime = null;
  }

  // Initialize the notification service
  async initialize() {
    // Request notification permission
    await this.requestNotificationPermission();
    
    // Start periodic checking
    this.startPeriodicChecks();
    
    // Check immediately on startup
    this.checkDrillReminders();
  }

  // Request browser notification permission
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      this.notificationPermission = permission;
      return permission === 'granted';
    }
    return false;
  }

  // Start periodic checks for drill reminders
  startPeriodicChecks() {
    // Check every 30 minutes
    this.checkInterval = setInterval(() => {
      this.checkDrillReminders();
    }, 30 * 60 * 1000);

    // Also check when window gains focus
    window.addEventListener('focus', () => {
      this.checkDrillReminders();
    });
  }

  // Stop periodic checks
  stopPeriodicChecks() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  // Main function to check for drill reminders
  checkDrillReminders(vesselId = 'default') {
    try {
      // Get notification settings
      const notificationSettings = legacyGet(null, 'notificationSettings', {});

      // Check if drill reminders are enabled
      if (!notificationSettings.drillReminders) {
        return;
      }

      // Get current vessel and drills
      const vessels = legacyGet(null, 'vessels', []);
      const currentVessel = vessels.find(v => v.id === vesselId);
      const drills = legacyGet(vesselId, 'drills', []);

      if (!currentVessel) {
        return;
      }

      // Import drill requirements (would need to be available)
      this.checkVesselDrillReminders(currentVessel, drills, notificationSettings);
    } catch (error) {
      logger.error('Error checking drill reminders:', error);
    }
  }

  // Check drill reminders for current vessel
  checkVesselDrillReminders(vessel, drills, settings) {
    // This would need the drill requirements logic
    // For now, let's implement a basic version
    
    const reminderDays = settings.reminderDays || 7;
    const now = new Date();
    const reminderThreshold = new Date(now.getTime() + (reminderDays * 24 * 60 * 60 * 1000));

    // Get the last notification check time to avoid spam
    const lastCheck = this.lastCheckTime || new Date(now.getTime() - (24 * 60 * 60 * 1000));
    this.lastCheckTime = now;

    // Basic drill types and their frequencies (simplified)
    const drillRequirements = {
      'fire_drill': { frequency: 7, name: 'Fire Drill' },        // Weekly
      'abandon_ship_drill': { frequency: 30, name: 'Abandon Ship Drill' }, // Monthly
      'mob_drill': { frequency: 30, name: 'Man Overboard Drill' }  // Monthly
    };

    Object.entries(drillRequirements).forEach(([drillType, requirement]) => {
      const lastDrill = drills
        .filter(drill => drill.type === drillType)
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

      if (!lastDrill) {
        // No drill of this type recorded - send immediate reminder
        this.sendNotification(
          'Drill Reminder',
          `${requirement.name} has never been recorded for ${vessel.name}`,
          'drill-overdue'
        );
        return;
      }

      const lastDrillDate = new Date(lastDrill.date);
      const daysSinceLastDrill = Math.floor((now - lastDrillDate) / (1000 * 60 * 60 * 24));
      const nextDueDate = new Date(lastDrillDate.getTime() + (requirement.frequency * 24 * 60 * 60 * 1000));
      const daysUntilDue = Math.floor((nextDueDate - now) / (1000 * 60 * 60 * 24));

      // Check if drill is overdue
      if (daysUntilDue < 0) {
        this.sendNotification(
          'Drill Overdue!',
          `${requirement.name} is overdue by ${Math.abs(daysUntilDue)} days on ${vessel.name}`,
          'drill-overdue'
        );
      }
      // Check if drill is due soon (within reminder threshold)
      else if (daysUntilDue <= reminderDays && nextDueDate > lastCheck) {
        this.sendNotification(
          'Drill Due Soon',
          `${requirement.name} is due in ${daysUntilDue} days for ${vessel.name}`,
          'drill-due-soon'
        );
      }
    });
  }

  // Send browser notification
  sendNotification(title, body, tag = 'drill-reminder') {
    if (this.notificationPermission === 'granted' && 'Notification' in window) {
      const notification = new Notification(title, {
        body,
        tag,
        icon: '/logo192.png', // Use app icon
        badge: '/logo192.png',
        requireInteraction: true, // Keep notification until user interacts
        actions: [
          {
            action: 'view',
            title: 'View Drills'
          },
          {
            action: 'dismiss',
            title: 'Dismiss'
          }
        ]
      });

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        // Navigate to drills page (would need routing integration)
        if (window.location.pathname !== '/') {
          window.location.hash = '#/drills';
        }
        notification.close();
      };

      // Auto-close after 10 seconds if not interacted with
      setTimeout(() => {
        notification.close();
      }, 10000);

      return notification;
    }
    return null;
  }

  // Get notification permission status
  getPermissionStatus() {
    return this.notificationPermission;
  }

  // Check if notifications are supported
  isSupported() {
    return 'Notification' in window;
  }

  // Cleanup
  destroy() {
    this.stopPeriodicChecks();
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;